import { ChangeDetectorRef, DestroyRef, Injectable, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable } from 'rxjs';

/** Sections of the shortcut sheet, in display order. */
export const SHORTCUT_GROUPS = ['General', 'Go to', 'Orders', 'Order', 'Document', 'Forms'] as const;
export type ShortcutGroup = (typeof SHORTCUT_GROUPS)[number];

export interface Shortcut {
  /**
   * `mod+k` (Ctrl, or ⌘ on a Mac), `shift+x`, `?`, `j`, or a sequence: `g o` is G then O.
   * Letters are lower case; shift is implied for symbols such as `?`.
   */
  keys: string;
  label: string;
  group: ShortcutGroup;
  run: () => void;
  /** Off while this returns false (e.g. nothing to save). */
  when?: () => boolean;
  /** Also while typing in a field: combos such as mod+enter. */
  inInput?: boolean;
  /** Also while a dialog or menu is open: the dialog's own shortcuts. */
  inOverlay?: boolean;
  /** Shown in the sheet instead of `keys`, e.g. `j or k`, `1 to 5` (`then`, `or`, `to` are words). */
  display?: string;
  /** Kept out of the sheet: aliases, and the rest of a range shown on its first entry. */
  hidden?: boolean;
  /** Offered in the command palette (default: when it is in the sheet). */
  palette?: boolean;
  /** Material icon for the command palette. */
  icon?: string;
  /** Keeps firing while the key is held (moving through rows or pages). Off by default: a held key runs once. */
  repeat?: boolean;
}

/** Something the command palette found for what was typed, e.g. an order by its number. */
export interface PaletteHit {
  label: string;
  context?: string;
  icon?: string;
  /** Opened with navigateByUrl. */
  url: string;
}

/**
 * Lets a feature answer typed text in the command palette (Servicing Transfer: an order number) without the
 * shared palette knowing about it. The palette only offers what `find` actually found.
 */
export interface PaletteLookup {
  /** Section title in the palette, e.g. "Order". */
  title: string;
  matches: (query: string) => boolean;
  find: (query: string) => Observable<PaletteHit | null>;
  /** Shown when nothing matched, e.g. "No order 99999 for this client. Check the number." */
  missing: (query: string) => string;
  /** Offered when the lookup itself failed, so a network error doesn't block the user. */
  fallback: (query: string) => PaletteHit;
}

/** A page from the side panel, for the command palette. */
export interface NavEntry {
  label: string;
  path: string;
  icon?: string;
  /** Parent group, e.g. "Products". */
  context?: string;
}

export interface KeyToken {
  key?: string;
  text?: string;
}

const SINGLE_KEYS_STORAGE_KEY = 'dcm.shortcuts.singleKeys';
const SEQUENCE_TIMEOUT_MS = 1200;
const OVERLAY_SELECTOR = [
  '.mat-mdc-dialog-container',
  '.mat-mdc-menu-panel',
  '.mat-mdc-select-panel',
  '.mat-mdc-autocomplete-panel',
  '.mat-datepicker-content'
]
  .map((panel) => `.cdk-overlay-container ${panel}`)
  .join(', ');
const WORDS = new Set(['then', 'or', 'to']);

/**
 * Keyboard shortcuts, app-wide. Pages register theirs with `registerShortcuts` (they go away with the
 * page), so the `?` sheet and the command palette always list exactly what works where the user is.
 */
@Injectable({ providedIn: 'root' })
export class ShortcutsService {
  private readonly document = inject(DOCUMENT);
  private readonly registry = signal<Shortcut[]>([]);
  readonly shortcuts = this.registry.asReadonly();

  /** Side-panel pages the user can open (published by the side panel). */
  readonly navigation = signal<NavEntry[]>([]);

  private readonly lookupRegistry = signal<PaletteLookup[]>([]);
  readonly lookups = this.lookupRegistry.asReadonly();

  /**
   * Single-key shortcuts (J, C, ? …) can be turned off: speech-input and screen-reader users type
   * letters to move around (WCAG 2.1.4). Combos such as Ctrl K keep working.
   */
  readonly singleKeysEnabled = signal(readFlag());

  readonly isMac = /Mac|iPhone|iPad/.test(globalThis.navigator?.platform ?? '');

  private pending: string | null = null;
  private pendingTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.document.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  /** Adds shortcuts until `destroyRef` is destroyed. Newer ones win over older ones on the same keys. */
  register(shortcuts: Shortcut[], destroyRef: DestroyRef): void {
    this.registry.update((list) => [...shortcuts, ...list]);
    destroyRef.onDestroy(() => this.registry.update((list) => list.filter((s) => !shortcuts.includes(s))));
  }

  /** Adds a command-palette lookup for the life of the app (features register theirs at startup). */
  registerLookup(lookup: PaletteLookup): void {
    this.lookupRegistry.update((list) => [...list, lookup]);
  }

  setSingleKeysEnabled(enabled: boolean): void {
    this.singleKeysEnabled.set(enabled);
    try {
      localStorage.setItem(SINGLE_KEYS_STORAGE_KEY, String(enabled));
    } catch {
      // Storage blocked: the choice lasts for this visit.
    }
  }

  isAvailable(shortcut: Shortcut): boolean {
    return (this.singleKeysEnabled() || !isSingleKey(shortcut.keys)) && (shortcut.when?.() ?? true);
  }

  /** Key caps for the sheet and hints: `g o` → G then O, `mod+k` → Ctrl K. */
  tokens(shortcut: Pick<Shortcut, 'keys' | 'display'>): KeyToken[] {
    const source = shortcut.display ?? shortcut.keys.split(' ').join(' then ');
    return source.split(' ').flatMap((part): KeyToken[] =>
      WORDS.has(part) ? [{ text: part }] : part.split('+').map((key) => ({ key: this.keyName(key) }))
    );
  }

  /** Plain-text hint for tooltips, e.g. "Ctrl+K", "G then O". */
  hint(keys: string): string {
    return keys
      .split(' ')
      .map((step) => step.split('+').map((key) => this.keyName(key)).join('+'))
      .join(' then ');
  }

  private keyName(key: string): string {
    switch (key) {
      case 'mod':
        return this.isMac ? '⌘' : 'Ctrl';
      case 'shift':
        return this.isMac ? '⇧' : 'Shift';
      case 'alt':
        return this.isMac ? '⌥' : 'Alt';
      case 'enter':
        return 'Enter';
      case 'escape':
        return 'Esc';
      default:
        return key.length === 1 ? key.toUpperCase() : key;
    }
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented || event.isComposing) return;
    const combo = comboOf(event);
    if (!combo) return;

    const typing = isTyping(event.target);
    const overlay = !!this.document.querySelector(OVERLAY_SELECTOR);
    const candidates = this.registry().filter(
      (s) =>
        (!event.repeat || s.repeat) && (!typing || s.inInput) && (!overlay || s.inOverlay) && this.isAvailable(s)
    );

    if (this.pending) {
      const sequence = `${this.pending} ${combo}`;
      this.clearPending();
      const hit = candidates.find((s) => s.keys === sequence);
      if (hit) {
        this.fire(hit, event);
        return;
      }
    }

    const hit = candidates.find((s) => s.keys === combo);
    if (hit) {
      this.fire(hit, event);
    } else if (candidates.some((s) => s.keys.startsWith(combo + ' '))) {
      this.pending = combo;
      this.pendingTimer = setTimeout(() => this.clearPending(), SEQUENCE_TIMEOUT_MS);
    }
  }

  private fire(shortcut: Shortcut, event: KeyboardEvent): void {
    event.preventDefault();
    shortcut.run();
  }

  private clearPending(): void {
    this.pending = null;
    clearTimeout(this.pendingTimer);
  }
}

/**
 * Registers shortcuts for the calling component, for as long as it lives. Call it in the constructor.
 * Each run marks the component for check, so plain-property state (zoneless) re-renders too.
 */
export function registerShortcuts(shortcuts: Shortcut[]): void {
  const service = inject(ShortcutsService);
  const cdr = inject(ChangeDetectorRef);
  service.register(
    shortcuts.map((s) => ({
      ...s,
      run: () => {
        s.run();
        cdr.markForCheck();
      }
    })),
    inject(DestroyRef)
  );
}

/** `mod+k`, `shift+j`, `?`, `enter`; null for a lone modifier. */
function comboOf(event: KeyboardEvent): string | null {
  if (['Control', 'Shift', 'Alt', 'Meta', 'AltGraph', 'CapsLock'].includes(event.key)) return null;
  const key = event.key === ' ' ? 'space' : event.key.toLowerCase();
  // Symbols already carry shift ("?" is Shift+/), so it isn't spelled out for them.
  const symbol = event.key.length === 1 && !/[a-z0-9]/i.test(event.key);
  const parts: string[] = [];
  if (event.ctrlKey || event.metaKey) parts.push('mod');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey && !symbol) parts.push('shift');
  parts.push(key);
  return parts.join('+');
}

/** Keys typed into a field, a dropdown or a list belong to it. */
function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.closest) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
  if (el.tagName === 'INPUT') {
    return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes((el as HTMLInputElement).type);
  }
  return !!el.closest('[role="combobox"], [role="listbox"], [role="menu"], [role="slider"]');
}

function isSingleKey(keys: string): boolean {
  return !/\b(mod|alt)\+/.test(keys);
}

function readFlag(): boolean {
  try {
    return localStorage.getItem(SINGLE_KEYS_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}
